import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { isAxiosError } from 'axios';
import {
  getMessageFromGeoEventSaveErrorResponse,
  getViewGeoEventLink,
} from 'features/geoEvents/geoEvent.utils';
import { GeoEventCreateUpdateData } from 'features/provisionalSubmissions/provisionalSubmissions.types';
import { logUnexpectedErrorAndNotifyUserWithTryAgain } from 'shared/handleUnexpectedError';
import { useConfirm } from 'components/ConfirmDialog';
import { useProvisionalSubmissionMutations } from '../useProvisionalSubmissionMutations';

type UseProvisionalGeoEventActionsOptions = {
  reviewSubmission?: { id: number };
  geoEvent?: { id: number };
  navigate: (path: string) => void;
  onSubmitForReviewSuccess?: () => void;
};

type UseProvisionalGeoEventActions = {
  submitForReview: (data: GeoEventCreateUpdateData) => Promise<void>;
  approveSubmission: (data: GeoEventCreateUpdateData) => Promise<void>;
  rejectSubmission: () => Promise<void>;
  isRejecting: boolean;
};

export const useProvisionalGeoEventActions = (
  options: UseProvisionalGeoEventActionsOptions,
): UseProvisionalGeoEventActions => {
  const { reviewSubmission, geoEvent, navigate, onSubmitForReviewSuccess } =
    options;
  const { t } = useTranslation();
  const confirm = useConfirm();
  const {
    submitProvisionalGeoEventCreate,
    submitProvisionalGeoEventEdit,
    approveProvisionalGeoEventCreate,
    approveProvisionalGeoEventEdit,
    rejectProvisionalSubmission,
    isRejecting,
  } = useProvisionalSubmissionMutations();

  const showSaveError = (
    error: unknown,
    opts?: { isProvisional: boolean },
  ): void => {
    if (isAxiosError(error)) {
      enqueueSnackbar(getMessageFromGeoEventSaveErrorResponse(error, t, opts), {
        variant: 'error',
      });
    } else {
      logUnexpectedErrorAndNotifyUserWithTryAgain(t, { error });
    }
  };

  const submitForReview = async (
    data: GeoEventCreateUpdateData,
  ): Promise<void> => {
    try {
      const result = geoEvent
        ? await submitProvisionalGeoEventEdit(data, geoEvent.id)
        : await submitProvisionalGeoEventCreate(data);
      // Null means the user declined the redundancy confirm; nothing was submitted.
      if (!result) return;
    } catch (error: unknown) {
      showSaveError(error, { isProvisional: true });
      return;
    }
    enqueueSnackbar(t('createEditWildfire.successMessage.provisional'), {
      variant: 'success',
    });
    if (onSubmitForReviewSuccess) {
      onSubmitForReviewSuccess();
    } else {
      navigate('/');
    }
  };

  const approveSubmission = async (
    data: GeoEventCreateUpdateData,
  ): Promise<void> => {
    if (!reviewSubmission) return;
    let approvalResponse;
    try {
      approvalResponse = geoEvent
        ? await approveProvisionalGeoEventEdit(
            reviewSubmission.id,
            data,
            geoEvent.id,
          )
        : await approveProvisionalGeoEventCreate(reviewSubmission.id, data);
      // Null means the reviewer declined the redundancy confirm; nothing was approved.
      if (!approvalResponse) return;
    } catch (error: unknown) {
      showSaveError(error);
      return;
    }
    enqueueSnackbar(t('reviewSubmission.snackbar.approved'), {
      variant: 'success',
    });
    navigate(
      getViewGeoEventLink(data.geoEventType, approvalResponse.geoEventId),
    );
  };

  const rejectSubmission = async (): Promise<void> => {
    if (!reviewSubmission) return;
    const confirmed = await confirm(
      t('reviewSubmission.rejectConfirm.message'),
      {
        title: t('reviewSubmission.rejectConfirm.title'),
      },
    );
    if (!confirmed) return;
    try {
      await rejectProvisionalSubmission(reviewSubmission.id);
    } catch (error: unknown) {
      showSaveError(error);
      return;
    }
    enqueueSnackbar(t('reviewSubmission.snackbar.rejected'), {
      variant: 'success',
    });
    navigate('/');
  };

  return { submitForReview, approveSubmission, rejectSubmission, isRejecting };
};
